/* ========================================================================
 * Apricot's input method Module
 * ========================================================================
 *
 * Function to track the current input method
 *
 * This plugin is written based on Jeremy Fields, what-input.js
 * https://github.com/ten1seven/what-input
 * ======================================================================== */

/**
 * InputMethod
 *
 * @export
 * @param {Element} data.elem
 * @param {Boolean} data.shadowRoot
 * @returns {void}
 *
 */
const InputMethod = (data = {}) => {
  // Check if we're in a browser environment
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return {
      init: () => {},
      types: () => [],
      input: () => 'initial',
      destroy: () => {},
    };
  }
  const defaultData = {
    elem: null,
    shadowRoot: false,
  };

  data = { ...defaultData, ...data };

  // last used input type
  let currentInput = 'initial';
  // last used input intent
  let currentIntent = null;
  // form input types
  const formInputs = ['input', 'select', 'textarea', ''];
  // list of modifier keys commonly used with the mouse and
  // can be safely ignored to prevent false keyboard detection
  const ignoreMap = [
    16, // shift
    17, // control
    18, // alt
    91, // Windows key / left Apple cmd
    93, // Windows menu / right Apple cmd
  ];
  // mapping of events to input types
  const inputMap = {
    keyup: 'keyboard',
    mousedown: 'mouse',
    mousemove: 'mouse',
    MSPointerDown: 'pointer',
    MSPointerMove: 'pointer',
    pointerdown: 'pointer',
    pointermove: 'pointer',
    touchstart: 'touch',
  };
  // list of all used input types
  let inputTypes = new Set();
  // boolean: true if touch buffer timer is running
  let isBuffering = false;
  // touch buffer timer
  let touchTimer = null;

  // cache document.documentElement
  let docElem = null;

  // updates the doc and `inputTypes` array with new input
  const _setInput = () => {
    docElem.setAttribute('data-cb-input-m', currentInput);

    docElem.setAttribute('data-cb-intent-m', currentInput);

    inputTypes.add(currentInput);
  };

  // ----- Utilities
  const _pointerType = event => {
    if (typeof event.pointerType === 'number') {
      return ignoreMap[event.pointerType];
    } else {
      return event.pointerType === 'pen' ? 'touch' : event.pointerType; // treat pen like touch
    }
  };

  // checks conditions before updating new input
  const _updateInput = event => {
    // only execute if the touch buffer timer isn't running
    if (!isBuffering) {
      var eventKey = event.which;
      var value = inputMap[event.type];
      if (value === 'pointer') value = _pointerType(event);

      if (currentInput !== value || currentIntent !== value) {
        var activeElem = document.activeElement;
        var activeInput =
          activeElem && activeElem.nodeName && formInputs.indexOf(activeElem.nodeName.toLowerCase()) === -1
            ? true
            : false;

        if (
          value === 'touch' ||
          // ignore mouse modifier keys
          (value === 'mouse' && ignoreMap.indexOf(eventKey) === -1) ||
          // don't switch if the current element is a form input
          (value === 'keyboard' && activeInput)
        ) {
          // set the current and catch-all variable
          currentInput = currentIntent = value;

          _setInput();
        }
      }
    }
  };

  // ----- Events

  // updates input intent for `mousemove` and `pointermove`
  const _setIntent = event => {
    // only execute if the touch buffer timer isn't running
    if (!isBuffering) {
      var value = inputMap[event.type];
      if (value === 'pointer') {
        value = _pointerType(event);
      }

      if (currentIntent !== value) {
        currentIntent = value;

        docElem.setAttribute('data-cb-intent-m', currentIntent);
      }
    }
  };

  // buffers touch events because they frequently also fire mouse events
  const _touchBuffer = event => {
    // clear the timer if it happens to be running
    window.clearTimeout(touchTimer);

    // set the current input
    _updateInput(event);

    // set the isBuffering to `true`
    isBuffering = true;

    // run the timer
    touchTimer = window.setTimeout(() => {
      // if the timer runs out, set isBuffering back to `false`
      isBuffering = false;
    }, 200);
  };

  // detect version of mouse wheel event to use
  // via https://developer.mozilla.org/en-US/docs/Web/Events/wheel
  const _detectWheel = () => {
    return 'onwheel' in document.createElement('div')
      ? 'wheel' // Modern browsers support "wheel"
      : document.onmousewheel !== undefined
        ? 'mousewheel' // Webkit and IE support at least "mousewheel"
        : 'DOMMouseScroll'; // let's assume that remaining browsers are older Firefox
  };

  // Add a destroy method to remove listeners
  let listeners = [];
  const _addListener = (target, type, handler, options) => {
    target.addEventListener(type, handler, options);

    listeners.push(() => target.removeEventListener(type, handler, options));
  };

  const _addListeners = () => {
    // `pointermove`, `MSPointerMove`, `mousemove` and mouse wheel event binding
    // can only demonstrate potential, but not actual, interaction
    // and are treated separately

    // pointer events (mouse, pen, touch)
    if (window.PointerEvent) {
      _addListener(docElem, 'pointerdown', _updateInput);

      _addListener(docElem, 'pointermove', _setIntent);
    } else if (window.MSPointerEvent) {
      _addListener(docElem, 'MSPointerDown', _updateInput);

      _addListener(docElem, 'MSPointerMove', _setIntent);
    } else {
      // mouse events
      _addListener(docElem, 'mousedown', _updateInput);

      _addListener(docElem, 'mousemove', _setIntent);

      // touch events
      if ('ontouchstart' in window) {
        _addListener(docElem, 'touchstart', _touchBuffer, { passive: true });
      }
    }

    // mouse wheel
    _addListener(docElem, _detectWheel(), _setIntent, { passive: true });

    // keyboard events
    _addListener(docElem, 'keydown', _updateInput);

    _addListener(docElem, 'keyup', _updateInput);
  };

  // -----
  const _init = () => {
    // add correct mouse wheel event mapping to `inputMap`
    inputMap[_detectWheel()] = 'mouse';

    if (data.shadowRoot) {
      docElem = data.elem;
    } else {
      docElem = document.documentElement;
    }

    _addListeners();

    _setInput();
  };

  const _destroy = () => {
    listeners.forEach(remove => remove());

    listeners = [];
  };

  // return current input type
  // 'strict' (default): returns the same value as the 'data-cb-input-m' attribute
  // 'loose': includes 'data-cb-intent-m' value if it's more current than 'data-cb-input-m'
  const _input = opt => {
    return opt === 'loose' ? currentIntent : currentInput;
  };

  // returns array: all the detected input types
  const _types = () => Array.from(inputTypes);

  // ----- cb Apricot input method module to public API

  // activate input listener
  // Auto run
  if ('addEventListener' in window) {
    _init();
  }

  // Expose the public methods
  return {
    init: _init,
    types: _types,
    input: _input,
    destroy: _destroy,
  };
};

export default InputMethod;
